<!--
  alert-banner.component.html

  Two shells (carousel / list) that both project the SAME alert-card
  template (#alertCard). In carousel mode, the pagination/prev/next/close
  controls render INSIDE the card's own header row (next to the timestamp)
  rather than as a separate outer toolbar — so there's a single bordered
  surface, matching the target design.
-->

<div
  class="alert-banner alert-banner--carousel"
  [class.alert-banner--elevated]="position !== 'static'"
  *ngIf="layout === 'carousel' && total > 0 && currentAlert as alert"
  [ngStyle]="wrapperStyles"
  cdkDrag
  [cdkDragDisabled]="!draggable"
  [cdkDragBoundary]="$any(dragBoundary)"
  [cdkDragLockAxis]="$any(dragLockAxis)"
  (cdkDragEnded)="onDragEnded($event)"
>
  <div class="alert-banner__drag-handle" *ngIf="draggable" cdkDragHandle aria-label="Drag to move">
    <i class="fas fa-grip-lines"></i>
  </div>
  <div class="alert-banner__heading" *ngIf="heading">{{ heading }}</div>
  <ng-container *ngTemplateOutlet="alertCard; context: { $implicit: alert, carousel: true }"></ng-container>
</div>

<div
  class="alert-banner alert-banner--list"
  [class.alert-banner--elevated]="position !== 'static'"
  *ngIf="layout === 'list' && total > 0"
  [ngStyle]="wrapperStyles"
  cdkDrag
  [cdkDragDisabled]="!draggable"
  [cdkDragBoundary]="$any(dragBoundary)"
  [cdkDragLockAxis]="$any(dragLockAxis)"
  (cdkDragEnded)="onDragEnded($event)"
>
  <div class="alert-banner__drag-handle" *ngIf="draggable" cdkDragHandle aria-label="Drag to move">
    <i class="fas fa-grip-lines"></i>
  </div>
  <div class="alert-banner__heading" *ngIf="heading">{{ heading }}</div>

  <ng-container *ngFor="let alert of alerts; trackBy: trackById">
    <ng-container *ngTemplateOutlet="alertCard; context: { $implicit: alert, carousel: false }"></ng-container>
  </ng-container>
</div>

<!-- Shared alert-card template: icon | title + meta / message / tag + actions -->
<ng-template #alertCard let-alert let-carousel="carousel">
  <div class="alert-card" [ngClass]="'alert-card--' + alert.type">
    <div class="alert-card__icon" [attr.data-tooltip]="alert.type" data-tooltip-conf="bottom">
      <i [class]="iconFor(alert)"></i>
    </div>

    <div class="alert-card__body">
      <div class="alert-card__header">
        <span class="alert-card__title" *ngIf="alert.title"  [attr.data-tooltip]="alert.titleTooltip ? alert.title : null"
          [attr.data-tooltip-conf]="alert.tooltipPosition || 'bottom'">
          {{
          alert.title && alert.titleMaxLength && alert.title.length > alert.titleMaxLength
          ? (alert.title | slice: 0:alert.titleMaxLength) + '...'
          : alert.title
          }}
        </span>
        <div class="alert-card__meta">
          <span class="alert-card__time" *ngIf="alert.timestamp">{{ alert.timestamp }}</span>

          <ng-container *ngIf="carousel">
            <span class="alert-card__pagination" *ngIf="total > 1">{{ currentIndex + 1 }} / {{ total }}</span>

            <button
              type="button"
              class="icon-btn"
              [disabled]="currentIndex === 0"
              (click)="prev()"
              aria-label="Previous alert"
            >
              <i class="fas fa-chevron-left"></i>
            </button>

            <button
              type="button"
              class="icon-btn"
              [disabled]="currentIndex === total - 1"
              (click)="next()"
              aria-label="Next alert"
            >
              <i class="fas fa-chevron-right"></i>
            </button>

            <button
              type="button"
              class="icon-btn"
              *ngIf="showClose"
              (click)="close()"
              aria-label="Close notifications"
            >
              <i class="fas fa-xmark"></i>
            </button>
          </ng-container>

          <button
            type="button"
            class="icon-btn"
            *ngIf="!carousel && alert.dismissible"
            (click)="onDismiss(alert)"
            [attr.aria-label]="'Dismiss ' + (alert.title || 'alert')"
          >
            <i class="fas fa-xmark"></i>
          </button>
        </div>
      </div>

      <p class="alert-card__message" [attr.data-tooltip]="alert.messageTooltip ? alert.message : null"
        [attr.data-tooltip-conf]="alert.tooltipPosition || 'bottom'">
        {{
        alert.messageMaxLength && alert.message.length > alert.messageMaxLength
        ? (alert.message | slice: 0:alert.messageMaxLength) + '...'
        : alert.message
        }}
      </p>
      <div class="alert-card__footer" *ngIf="alert.tag || alert.actions?.length">
        <span
          class="alert-card__tag"
          *ngIf="alert.tag as tag"
          [style.color]="tag.color || null"
          [style.borderColor]="tag.color || null"
        >
          {{ tag.label }}
        </span>

        <div class="alert-card__actions" *ngIf="alert.actions?.length">
          <button
            *ngFor="let action of alert.actions"
            type="button"
            class="alert-btn"
            [ngClass]="'alert-btn--' + (action.style || 'link')"
            [disabled]="action.disabled"
            (click)="onAction(alert, action)"
          >
            <i *ngIf="action.icon" [class]="action.icon"></i>
            {{ action.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-template>